import React, { useEffect, useState, useRef } from "react";

/**
 * Typewriter
 *  - Reveals `text` character-by-character.
 *  - `trigger` controls when typing starts (e.g., set true on hover).
 *  - Once a given `id` has been fully typed in the current session,
 *    it is marked as "done" in sessionStorage and on remount it shows
 *    the full text immediately without animating again.
 */
const Typewriter = ({ id, text = "", trigger = false, speed = 16, className = "" }) => {
  const sessionKey = `tw_done_${id}`;
  const alreadyDone = typeof window !== "undefined" && sessionStorage.getItem(sessionKey) === "1";
  const [output, setOutput] = useState(alreadyDone ? text : "");
  const [done, setDone] = useState(alreadyDone);
  const indexRef = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (done) return;
    if (!trigger) return;
    if (intervalRef.current) return; // already running
    indexRef.current = output.length;
    intervalRef.current = setInterval(() => {
      indexRef.current += 1;
      const next = text.slice(0, indexRef.current);
      setOutput(next);
      if (indexRef.current >= text.length) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setDone(true);
        try { sessionStorage.setItem(sessionKey, "1"); } catch {}
      }
    }, speed);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, text]);

  // If text changes (admin edit) reset
  useEffect(() => {
    if (alreadyDone) {
      setOutput(text);
      setDone(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <p className={className}>
      {output}
      {!done && trigger && <span className="inline-block w-[2px] h-[1em] align-middle ml-1 bg-ahm-orange animate-pulse" />}
    </p>
  );
};

export default Typewriter;
